import { useRef, useState } from "react"
import { Check, Eye, Loader2, Trash2, Upload } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { DocPreviewDialog } from "@/features/transit/components/doc-preview-dialog"
import { UploadMetadataDialog } from "@/features/transit/components/upload-metadata-dialog"
import {
  useDeleteSlotDoc,
  useUpsertSlotDoc,
} from "@/features/transit/hooks/use-doc-mutations"
import { useTransitUpload } from "@/features/transit/hooks/use-transit-upload"
import { useUploadWithMetadata } from "@/features/transit/hooks/use-upload-with-metadata"
import { indexDocsBySlot } from "@/features/transit/lib/doc-slots"
import { isOTLocked } from "@/features/transit/lib/ot-math"
import { cn } from "@/lib/utils"
import type { OT, OTDocSlotId } from "@/features/transit/api/types"

interface DocSlotCardProps {
  ot: OT
  slotId: OTDocSlotId
  // Optional override for the user-facing label; defaults to
  // `transit:docLabels.<slotId>`.
  hint?: string
}

// One uniform tile for any of the 16 slot docs. Filled state shows a
// dark green tick on a neutral card; empty state has a dashed border +
// upload button. Clicking on a filled card opens an inline preview;
// no separate download button — the preview dialog has its own.
//
// `draftBL` is special — uploading it is a different endpoint that
// also clears OK pour impression, so we delegate to the panel rather
// than handling it here.
export function DocSlotCard({ ot, slotId, hint }: DocSlotCardProps) {
  const { t } = useTranslation("transit")
  const docs = indexDocsBySlot(ot.docs)
  const doc = docs[slotId]

  const upload = useTransitUpload()
  const upsert = useUpsertSlotDoc(ot.id)
  const remove = useDeleteSlotDoc(ot.id)
  const [previewOpen, setPreviewOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const meta = useUploadWithMetadata({
    kindLabel: t(`docLabels.${slotId}`, { defaultValue: slotId }),
    upload: async (file, m) => {
      await upload.upload({
        otId: ot.id,
        kind: "slotDoc",
        slotId,
        file,
        register: async ({ storedKey, file: f }) =>
          upsert.mutateAsync({
            slotId,
            payload: {
              name: m.name,
              storedKey,
              docType: f.type || undefined,
              size: f.size,
              uploadedBy: m.uploadedBy || undefined,
              note: m.description || undefined,
            },
          }),
      })
    },
  })

  const filled = !!doc
  const isUploading = upload.isUploading || upsert.isPending
  const locked = isOTLocked(ot)

  // Single hidden input drives the whole empty card — clicking the
  // dashed surface (or the Upload button inside it) triggers the same
  // picker. Filled cards don't get a card-level click target since
  // their inner buttons (preview / delete) cover the actions. Locked
  // OTs disable the picker entirely.
  const triggerPicker = () => {
    if (isUploading || locked) return
    fileInputRef.current?.click()
  }

  const cardClassName = cn(
    "group flex items-center gap-3 rounded-md border px-3 py-2 transition-colors",
    filled
      ? "bg-card hover:bg-muted/30"
      : locked
        ? "border-dashed bg-muted/30"
        : "border-dashed bg-card hover:bg-muted/30 cursor-pointer text-left w-full",
  )

  const cardBody = (
    <>
      {filled ? (
        <span className="bg-emerald-600 text-white flex size-4 flex-shrink-0 items-center justify-center rounded-full">
          <Check className="size-2.5" />
        </span>
      ) : (
        <span className="border-muted-foreground/40 size-4 flex-shrink-0 rounded-full border-2" />
      )}

      <div className="min-w-0 flex-1">
        <div className="text-foreground truncate text-sm font-medium">
          {t(`docLabels.${slotId}`, { defaultValue: slotId })}
        </div>
        {filled && doc ? (
          <>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="text-muted-foreground hover:text-foreground mt-0.5 flex w-full items-center gap-1 text-left text-[11px] tabular-nums hover:underline"
            >
              <span className="truncate font-mono">{doc.name}</span>
              <span className="text-muted-foreground/70 flex-shrink-0">
                · {doc.uploadedAt?.slice(0, 10) ?? "—"}
                {doc.uploadedBy ? ` · ${doc.uploadedBy}` : ""}
              </span>
            </button>
            {doc.note ? (
              <p className="text-muted-foreground/80 mt-0.5 line-clamp-2 text-[11px]">
                {doc.note}
              </p>
            ) : null}
          </>
        ) : hint ? (
          <p className="text-muted-foreground mt-0.5 text-[11px]">{hint}</p>
        ) : null}
      </div>

      <div className="flex flex-shrink-0 items-center gap-0.5">
        {filled && doc ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => setPreviewOpen(true)}
                  aria-label={t("preview.btn")}
                >
                  <Eye className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("preview.btn")}</TooltipContent>
            </Tooltip>
            {locked ? null : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 hover:text-red-600"
                    onClick={() => remove.mutate(slotId)}
                    disabled={remove.isPending}
                    aria-label={t("docCard.delete")}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("docCard.replaceHint")}</TooltipContent>
              </Tooltip>
            )}
          </>
        ) : locked ? (
          <span className="text-muted-foreground/70 text-[11px] italic">
            {t("locked.notProvided")}
          </span>
        ) : (
          <span
            className={cn(
              "border-input bg-background pointer-events-none inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium",
              isUploading && "opacity-60",
            )}
          >
            {isUploading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Upload className="size-4" />
            )}
            {t("docCard.upload")}
          </span>
        )}
      </div>
    </>
  )

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) meta.onPick(file)
          if (fileInputRef.current) fileInputRef.current.value = ""
        }}
      />
      {filled || locked ? (
        <div className={cardClassName}>{cardBody}</div>
      ) : (
        <button
          type="button"
          onClick={triggerPicker}
          disabled={isUploading}
          className={cardClassName}
        >
          {cardBody}
        </button>
      )}

      <UploadMetadataDialog
        pending={meta.pending}
        onCancel={meta.onCancel}
        onConfirm={meta.onConfirm}
      />

      <DocPreviewDialog
        otId={ot.id}
        doc={previewOpen && doc ? doc : null}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  )
}
